// Cast-Facade: waehlt anhand des Geraetetyps die passende Implementierung
// (Samsung Tizen bzw. LG WebOS) und stellt eine einheitliche API bereit.
//
// Struktur analog zu helpers/TV und helpers/Device.
//
// Support: Cast/OTT laeuft nur auf neueren Geraeten
//   - Samsung Tizen >= 6.5
//   - LG WebOS >= 23 (natives Cast-Pairing erst ab WebOS >= 25)
// Aeltere Geraete (Orsay, WebOS 3.5) werden bewusst ausgeschlossen.

import DeviceType from '@/helpers/DeviceType';
import CastTizen from './Tizen';
import CastLG, { LG_GOOGLE_CAST_APP_IDS } from './LG';

// Google Cast als installierbare App auf Samsung. Die Id stammt aus der
// Samsung-Referenz-App APPNetGC (Google-Cast-Training) und wird dort mit
// demselben SI-Token freigeschaltet wie Netflix. Sie ist KEINE Kachel auf der
// Cast-Seite, sondern nur ein Installationsziel auf /info-apps.
export const TIZEN_GOOGLE_CAST_APP_ID = '3202408036558';

// Temporaerer Netflix/Samsung-Token (wird spaeter ersetzt).
export const DEFAULT_CAST_TOKEN =
  '2whlIdCBODx7dRSI93fuPDLKuImD3OaAOLQKoe7Q2fvpcFENQLFhzFcgF1JY4OmE';

// Bekannte OTT-Apps mit App-Ids je Plattform. Die Tizen-App-Ids stammen aus dem
// Samsung-Katalog (Netflix/YouTube sind aus dem Training belegt; die uebrigen
// sind die gaengigen Store-Ids und sollten pro Kette verifiziert werden).
//
// Mehrere Ids je App: Samsung hat fuer viele Apps um 2020 neue Store-Ids
// vergeben, und welche ein Geraet fuehrt, haengt an Modelljahr und Firmware.
// Dafuer steht in `tizenAppIds` (bzw. `lgAppIds`) eine Liste - aelteste zuerst,
// neueste und wahrscheinlichste ZULETZT. Versucht wird von hinten: eine Id, die
// am Geraet installiert ist, gewinnt; sonst die neueste, die das Geraet kennt
// (siehe chooseAppId). `tizenAppId` bleibt fuer Eintraege mit genau einer Id.
//
// Quelle der Ids: die clone.xml eines laufenden TV (<widget id=...>) ist belegt;
// die uebrigen stammen aus Community-Listen (samsung-tv-ws-api,
// homebridge-samsung-tizen) und sind nur aufgenommen, wo mehrere Listen
// uebereinstimmen. Eine offizielle Samsung-Liste gibt es nicht.
//
// Sprachvarianten: auf Samsung gibt es fuer manche Apps je Geraetesprache eine
// eigene App-Id. Dafuer kann pro Eintrag eine Map hinterlegt werden:
//   tizenAppIdByLang: { de: '...', fr: '...' }
//   lgAppIdByLang:    { de: '...' }
// Passt die aktive Sprache, gewinnt der Eintrag aus der Map, sonst greift die
// allgemeine Id. Die Maps sind absichtlich leer - die sprachspezifischen Ids
// muessen pro Kette/Region ermittelt und hier (oder ueber device.castApps)
// eingetragen werden.
//
// Stand der LG-Ids: youtube.leanback.v4 ist auf einem 43UK660H im App-Katalog
// bestaetigt. Netflix, Prime Video, Disney+ und Paramount+ stehen auf diesem
// Geraet NICHT im Katalog und sind dort folglich auch nicht installierbar - die
// Ids bleiben trotzdem drin, weil sie je Modell/Region vorhanden sein koennen.
// Die Install-Seite (/info-apps) zeigt pro App an, was das Geraet dazu meldet.
//
// LG hat 08/2026 bestaetigt: die vier Premium-OTT-Apps gehoeren NICHT zum
// Default-CP-Katalog der Hospitality-Firmware, und welche CP-Apps ein Geraet
// kennt, haengt an Modell und Region. Sie lassen sich deshalb nicht einfach
// nachinstallieren - noetig ist ein Vertrags-/Lizenzprozess ueber den lokalen
// LG-Sales-Engineer, aus dem app-spezifische Tokens (bei Netflix zwingend) und
// die zugehoerigen Setup-Dokumente kommen. Die Geraetesprache spielt dabei
// keine Rolle; entscheidend sind Modell und Service-Country.
// Basis-URL fuer die Kachel-Logos (SVG). Pro App-Eintrag steht in `logo` die
// fertige URL; ueber device.castApps ist sie pro Geraet ueberschreibbar.
export const CAST_LOGO_BASE = 'https://media.digmedia.net/cast/';

// Wie kommt das Handy an die App?
//
//  'googlecast' - ueber Google Cast bzw. DIAL. Das Handy waehlt die App selbst
//                 aus, der TV startet sie. Diese Apps brauchen KEINE eigene
//                 Kachel; die generische Cast-Kachel deckt sie alle ab.
//  'direct'     - die App bringt ihre eigene Anmeldung mit (Netflix-Code,
//                 Spotify Connect). Hier hilft nur: App starten, den Rest
//                 macht die App. Eigene Kachel, die SOFORT startet.
//
// Bei Netflix ist der Sofortstart nicht nur sinnvoll, sondern Auflage: das
// Netflix Integration Compliance Agreement for Hotels (Exhibit 1) verlangt,
// dass das Netflix-Icon "directly connect users to the Netflix Service ...
// without any intervening screens". Zwischen Kachel und App darf also nichts
// stehen - die SoftAP-Seite laeuft deshalb VOR der Kachelauswahl.
export const PAIRING_MODE = {
  GOOGLECAST: 'googlecast',
  DIRECT: 'direct',
};

// Generische Cast-Kachel. Startet bewusst keine App: bei Google Cast entscheidet
// das Handy, welche App der TV oeffnet.
export const CAST_TILE = {
  key: 'cast',
  name: 'Cast',
  pairing: PAIRING_MODE.GOOGLECAST,
  color: '#4285f4',
  // Bewusst ohne logo: unter CAST_LOGO_BASE liegt noch kein Google-Cast-Logo.
  // Die Kachel zeigt so lange den Namen. Sobald Logo_GoogleCast.svg hochgeladen
  // ist, hier `logo: CAST_LOGO_BASE + 'Logo_GoogleCast.svg'` ergaenzen.
};

// Vollstaendiger Katalog. Was davon als Kachel erscheint, steuert
// DEFAULT_CAST_APPS bzw. device.castApps - der Katalog bleibt die Quelle fuer
// App-Ids und die Installationsseite.
export const CAST_APP_CATALOG = [
  CAST_TILE,
  {
    key: 'netflix',
    name: 'Netflix',
    tizenAppId: '3201907018807',
    lgAppId: 'netflix',
    // Netflix kann nicht per Google Cast angesteuert werden und bringt eine
    // eigene Anmeldung mit - siehe PAIRING_MODE.
    pairing: PAIRING_MODE.DIRECT,
    // aliases dienen der Namenssuche im App-Katalog des Geraets, falls die
    // konfigurierte Id dort nicht existiert (Ids sind modell-/regionsabhaengig).
    aliases: ['netflix'],
    color: '#e50914',
    logo: CAST_LOGO_BASE + 'Logo_Netflix.svg',
  },
  {
    key: 'youtube',
    name: 'YouTube',
    tizenAppId: '111299001912',
    // LG: im App-Katalog eines 43UK660H bestaetigt.
    lgAppId: 'youtube.leanback.v4',
    tizenAppIdByLang: {},
    aliases: ['youtube'],
    color: '#ff0000',
    logo: CAST_LOGO_BASE + 'Logo_Youtube.svg',
  },
  {
    key: 'amazon',
    name: 'Prime Video',
    // Die neue Id ist auf einem U800F (Tizen 9) in der clone.xml belegt.
    tizenAppIds: ['3201512006785', '3201910019365'],
    lgAppId: 'amazon.html',
    aliases: ['amazon', 'prime video', 'primevideo'],
    color: '#1399FF',
    logo: CAST_LOGO_BASE + 'Logo_PrimeVideo.svg',
  },
  {
    key: 'disney',
    name: 'Disney+',
    // 3201901017640 ist die Id bis ca. 2019; neuere Geraete fuehren eine der
    // beiden spaeteren.
    tizenAppIds: ['3201901017640', '3202009021709', '3202204027038'],
    lgAppId: 'com.disney.disneyplus-prod',
    aliases: ['disney'],
    color: '#113ccf',
    logo: CAST_LOGO_BASE + 'Logo_DisneyPlus.svg',
  },
  {
    key: 'paramount',
    name: 'Paramount+',
    // Die fruehere Id 3201911020367 stand in keiner Quelle und ist entfernt.
    // 3201710014981 ist die App aus CBS-All-Access-Zeiten.
    tizenAppIds: ['3201710014981', '3202110025305'],
    lgAppId: 'com.cbs.ott',
    aliases: ['paramount', 'cbs'],
    color: '#0064ff',
    logo: CAST_LOGO_BASE + 'Logo_ParamountPlus.svg',
  },
  // Apple TV, DAZN und Joyn: Ids aus den Community-Listen, noch nicht an einem
  // Geraet belegt. Findet sich keine davon, sucht der Start die App ueber
  // aliases unter den installierten Apps des TV (Cast.findInstalledAppId).
  // Logos folgen dem Namensschema unter CAST_LOGO_BASE; fehlt die Datei, zeigt
  // die Kachel den Namen.
  {
    key: 'appletv',
    name: 'Apple TV',
    tizenAppId: '3201807016597',
    lgAppId: '',
    aliases: ['apple tv', 'appletv'],
    color: '#000000',
    logo: CAST_LOGO_BASE + 'Logo_Apple-TV.svg',
  },
  {
    key: 'dazn',
    name: 'DAZN',
    // 3201607009920 stammt von einem deutschen TV (aeltere DE-Variante).
    tizenAppIds: ['3201607009920', '3201806016390'],
    lgAppId: '',
    aliases: ['dazn'],
    color: '#f7ff1a',
    logo: CAST_LOGO_BASE + 'Logo_DAZN.svg',
  },
  {
    key: 'joyn',
    name: 'Joyn',
    tizenAppId: '3201502001386',
    lgAppId: '',
    aliases: ['joyn'],
    color: '#333333',
    logo: CAST_LOGO_BASE + 'Logo_Joyn.svg',
  },
  {
    key: 'spotify',
    name: 'Spotify',
    // Tizen-Id in allen Listen gleich; die LG-Id ist noch zu verifizieren.
    tizenAppId: '3201606009684',
    lgAppId: 'com.4795741.189428',
    // Spotify Connect ist ein eigenes Protokoll - App starten, Rest macht Spotify.
    pairing: PAIRING_MODE.DIRECT,
    aliases: ['spotify'],
    color: '#1db954',
    logo: CAST_LOGO_BASE + 'Logo_Spotify.svg',
  },
  // Weitere Apps fuer den DACH-Raum. Eingetragen sind nur Ids, die in mehreren
  // Listen uebereinstimmen (ARD: aus der clone.xml eines U800F). Eine geratene
  // Id ist schlimmer als keine - der Start liefe gegen die falsche App. Ohne Id
  // (WOW, MagentaTV, waipu.tv, Zattoo, Crunchyroll) werden sie ueber aliases
  // unter den installierten Apps des TV gefunden (Cast.findInstalledAppId).
  // Ohne logo: die Kachel zeigt den Namen, bis die Datei unter CAST_LOGO_BASE
  // liegt. Kachel werden sie nur, wenn device.castApps, der Menuepunkt
  // (#cast?apps=...) oder buildingApps.js sie nennt - die Install-Seite prueft
  // sie immer.
  //
  // aliases: der spezifischste zuerst, und nie so kurz, dass er in fremden
  // Namen steckt ("ard" allein traefe die ARD Audiothek, siehe appMatch.js).
  // Schluessel bewusst nicht 'ard'/'zdf'/'arte': ein Menuepunkt mit genau
  // diesem ident (z.B. ein Sender) wuerde sonst zum App-Start (ottAppKey in
  // store/modules/navigation.js vergleicht exakt mit den Katalog-Schluesseln).
  { key: 'ardmediathek', name: 'ARD Mediathek', tizenAppId: '3201412000679', lgAppId: '', aliases: ['ard mediathek', 'ardmediathek'], color: '#003480' },
  { key: 'zdfmediathek', name: 'ZDFmediathek', tizenAppId: '3201705012365', lgAppId: '', aliases: ['zdf mediathek', 'zdfmediathek', 'zdf'], color: '#fa7d19' },
  { key: 'artetv', name: 'arte', tizenAppIds: ['111399001295', '3202011022316'], lgAppId: '', aliases: ['arte'], color: '#fd4600' },
  { key: 'rtlplus', name: 'RTL+', tizenAppId: '3201908018988', lgAppId: '', aliases: ['rtl+', 'rtl plus', 'rtlplus'], color: '#0f1a3c' },
  { key: 'wow', name: 'WOW', tizenAppId: '', lgAppId: '', aliases: ['wow', 'sky ticket', 'skyticket'], color: '#0a0a0a' },
  { key: 'magentatv', name: 'MagentaTV', tizenAppId: '', lgAppId: '', aliases: ['magentatv', 'magenta tv'], color: '#e20074' },
  { key: 'waipu', name: 'waipu.tv', tizenAppId: '', lgAppId: '', aliases: ['waipu'], color: '#ff5a00' },
  { key: 'zattoo', name: 'Zattoo', tizenAppId: '', lgAppId: '', aliases: ['zattoo'], color: '#ff8c00' },
  { key: 'plex', name: 'Plex', tizenAppId: '3201512006963', lgAppId: '', aliases: ['plex'], color: '#e5a00d' },
  { key: 'twitch', name: 'Twitch', tizenAppId: '3202203026841', lgAppId: '', aliases: ['twitch'], color: '#9146ff' },
  { key: 'crunchyroll', name: 'Crunchyroll', tizenAppId: '', lgAppId: '', aliases: ['crunchyroll'], color: '#f47521' },
  { key: 'deezer', name: 'Deezer', tizenAppId: '3201608010191', lgAppId: '', aliases: ['deezer'], color: '#a238ff' },
  { key: 'amazonmusic', name: 'Amazon Music', tizenAppId: '3201710014874', lgAppId: '', aliases: ['amazon music', 'amazonmusic'], color: '#25d1da' },
  { key: 'applemusic', name: 'Apple Music', tizenAppId: '3201908019041', lgAppId: '', aliases: ['apple music', 'applemusic'], color: '#fa243c' },
  { key: 'tunein', name: 'TuneIn', tizenAppId: '121299000101', lgAppId: '', aliases: ['tunein'], color: '#14d8cc' },
];

// Sichtbare Kacheln in der Voreinstellung.
//
// Netflix steht NICHT in der Cast-Liste: es hat im Hauptmenue seine eigene
// Kachel (ident=netflix -> /cast?app=netflix) und wuerde hier nur doppelt
// auftauchen. Jede Kachel startet beim OK direkt ihre App - der Gast kann sich
// dort am TV anmelden oder vom Handy aus casten.
//
// Die generische Cast-Kachel (CAST_TILE) ist entfallen: sie startete nichts,
// sondern fuehrte nur auf eine Hinweisseite. Der Hinweis steht jetzt direkt
// auf der Kachelseite. Steht 'cast' noch in einem device.castApps, wird es
// uebergangen (siehe resolveApps im Store). Andere Zusammenstellungen ueber
// device.castApps, z.B. ["netflix","youtube","spotify"].
//
// Spotify steht nicht mehr drin: Spotify Connect laeuft vom Handy aus ohne
// Kachel. Mehr als vier Kacheln sind kein Problem - die Kachelseite scrollt.
const DEFAULT_CAST_KEYS = ['youtube', 'amazon', 'disney', 'appletv', 'dazn', 'paramount', 'joyn'];
export const DEFAULT_CAST_APPS = DEFAULT_CAST_KEYS.map((key) =>
  CAST_APP_CATALOG.find((a) => a.key === key)
).filter(Boolean);

function impl() {
  if (DeviceType.isTizen || DeviceType.isOrsay) return CastTizen;
  if (DeviceType.isLG) return CastLG;
  return null;
}

// Liefert die App-Id fuer Plattform und Sprache. Eine sprachspezifische Id aus
// tizenAppIdByLang / lgAppIdByLang gewinnt vor der allgemeinen.
//
// lang wird bewusst uebergeben und nicht hier aus dem Store gelesen: helpers/Cast
// wird vom cast-Store-Modul importiert, ein Store-Import hier wuerde den Zyklus
// store -> cast -> Cast -> store schliessen.
export function resolveAppId(app, platform, lang) {
  return appIdCandidates(app, platform, lang)[0] || '';
}

// Alle Ids einer App in Versuchsreihenfolge: die Sprachvariante zuerst, dann die
// Liste von hinten (neueste zuerst), dann die einzelne Id. Doppelte zaehlen
// einmal.
export function appIdCandidates(app, platform, lang) {
  if (!app) return [];
  const lg = platform === 'lg';
  const byLang = lg ? app.lgAppIdByLang : app.tizenAppIdByLang;
  const list = (lg ? app.lgAppIds : app.tizenAppIds) || [];
  const single = lg ? app.lgAppId : app.tizenAppId;
  const out = [];
  const add = (id) => {
    if (id && out.indexOf(String(id)) === -1) out.push(String(id));
  };
  if (byLang && lang) add(byLang[lang]);
  for (let k = list.length - 1; k >= 0; k -= 1) add(list[k]);
  add(single);
  return out;
}

// Welche der Ids nehmen? `states` ist je Kandidat das, was das Geraet meldet
// ({ id, state, resolvedAppId? } in Versuchsreihenfolge, state wie in
// getInstallPlan). Installiert gewinnt, dann installierbar, dann blockiert;
// meldet das Geraet zu keiner etwas Brauchbares, bleibt es bei der neuesten.
export function chooseAppId(states) {
  const list = states || [];
  const rank = ['installed', 'installable', 'blocked'];
  for (let r = 0; r < rank.length; r += 1) {
    for (let k = 0; k < list.length; k += 1) {
      if (list[k] && list[k].state === rank[r]) return list[k];
    }
  }
  return list[0] || null;
}

export default class Cast {
  static get platform() {
    if (DeviceType.isTizen || DeviceType.isOrsay) return 'tizen';
    if (DeviceType.isLG) return 'lg';
    return 'default';
  }

  // Pairing-Strategie der Plattform:
  //  - 'softap-qr' (Samsung Tizen): eigener SoftAP + selbst erzeugter WLAN-QR.
  //  - 'lg-native-cast' (LG WebOS >= 25): der TV bringt sein eigenes Cast-/
  //    AirPlay-Pairing mit (eigener Code/QR im nativen Overlay). Die App baut
  //    KEIN eigenes Pairing nach, sondern uebergibt nur dorthin.
  //  - 'network-qr' (LG < 25 mit WLAN): QR auf das verbundene WLAN, damit das
  //    Handy im selben Netz landet; die Cast-Discovery findet den TV dann selbst.
  //  - 'softap-qr' auch auf LG: seit der Klaerung, dass SSID (Property tv_name)
  //    und Passwort (soft_ap_password) setzbar sind, laesst sich der eigene
  //    SoftAP auch dort aufspannen. Bewusst NACH 'network-qr': im Hausnetz hat
  //    das Handy weiter Internet, am SoftAP haengt es nur am TV.
  //  - 'screenshare' (LG ohne alles): native Screen-Share-(Miracast-)App.
  //
  // Vor dem Abschluss von detect() wird nur nach Version entschieden, danach
  // zaehlt zusaetzlich, was das Geraet tatsaechlich gemeldet hat.
  static castStrategy() {
    if (this.platform !== 'lg') return 'softap-qr';
    const caps = CastLG.capabilities();

    // Eigener SoftAP ZUERST, auch wenn der TV ein natives Cast-Pairing
    // mitbringt. Vorgabe: das Handy soll in jedem Fall erst am TV haengen,
    // bevor es weitergeht - sonst gibt es auf LG gar keinen Verbindungsschritt
    // und die Cast-Seite zeigt nur einen Hinweistext.
    //
    // Der Preis ist bekannt: softap-qr ist die einzige Strategie, die etwas am
    // TV VERSTELLT. Auf LG ist die SSID die Property tv_name und damit zugleich
    // der Geraetename in Cast-/AirPlay-Listen - der TV heisst danach also wie
    // die SSID. Im Hotel ist das eher hilfreich (der Gast erkennt sein Zimmer);
    // wer einen festen Namen braucht, setzt device.softAP.ssid.
    //
    // isSupported() ist hier Pflicht, nicht doppelt gemoppelt: alte Geraete
    // (WebOS 3.5/5.0) melden die soft_ap-Properties teils ebenfalls - ohne
    // diese Schranke wuerde die Cast-Seite dort anfangen, den TV
    // umzukonfigurieren, obwohl Cast gar nicht unterstuetzt ist. Lesende
    // Strategien brauchen die Schranke nicht.
    if (caps.detected && caps.softApQr && CastLG.isSupported()) return 'softap-qr';

    // Kann das Geraet keinen eigenen SoftAP, bleibt es beim bisherigen Weg.
    if (CastLG.isNativeCastSupported()) return 'lg-native-cast';
    if (caps.detected && caps.networkQr) return 'network-qr';
    return 'screenshare';
  }

  // Ermittelt die Geraete-Faehigkeiten (nur LG; sonst sofort leeres Ergebnis).
  static detect(force) {
    const i = impl();
    if (i && typeof i.detect === 'function') return i.detect(force);
    return Promise.resolve(null);
  }

  static capabilities() {
    const i = impl();
    if (i && typeof i.capabilities === 'function') return i.capabilities();
    return null;
  }

  // Startet (nur LG) die native Screen-Share-App; sonst no-op.
  static launchScreenShare() {
    const i = impl();
    if (i && typeof i.launchScreenShare === 'function') return i.launchScreenShare();
    return Promise.reject(new Error('launchScreenShare not supported'));
  }

  // Google-Cast-ToS beim App-Start akzeptieren (nur Samsung, siehe
  // CastTizen.gcAcceptTermsWhenReady). Ohne Google Cast ein No-op.
  static acceptCastTermsAtStart() {
    const i = impl();
    if (i && typeof i.gcAcceptTermsWhenReady === 'function') return i.gcAcceptTermsWhenReady();
    return Promise.resolve(false);
  }

  // Uebergibt an den nativen Cast-Einstieg des TV (LG >= 25). Auf Tizen faellt
  // das auf die Google-Cast-Settings-Seite zurueck.
  // Tizen: laeuft beim Zurueckkehren in die tv-app noch ein Cast, wird er mit
  // finalize beendet (siehe CastTizen.gcStopIfCasting). Sonst No-op.
  static stopCastOnReturn() {
    const i = impl();
    if (i && typeof i.gcStopIfCasting === 'function') return i.gcStopIfCasting();
    return null;
  }

  // Tizen: Daten des Google-Cast-Dienstes loeschen (Gastwechsel). Sonst No-op.
  static clearCastData() {
    const i = impl();
    if (i && typeof i.gcClearData === 'function') return i.gcClearData();
    return Promise.resolve(false);
  }

  static launchCastEntry() {
    const i = impl();
    if (i && typeof i.launchCastEntry === 'function') return i.launchCastEntry();
    if (i && typeof i.gcLaunchSettings === 'function') {
      // Tizen: Promise<boolean>, wartet vorher auf das Ende von INIT.
      return Promise.resolve(i.gcLaunchSettings());
    }
    return Promise.reject(new Error('launchCastEntry not supported'));
  }

  // WLAN-Zugangsdaten des verbundenen Netzes (nur LG) fuer den 'network-qr'.
  static getNetworkQrCredentials() {
    const i = impl();
    if (i && typeof i.getNetworkQrCredentials === 'function') return i.getNetworkQrCredentials();
    return Promise.resolve({ ssid: '', key: '', security: 'nopass' });
  }

  // Ist Cast auf diesem Geraet grundsaetzlich moeglich?
  static isSupported() {
    const i = impl();
    if (!i) {
      // Browser/Default: fuer Entwicklung/Preview erlauben, aber ohne echte APIs.
      return process.env.NODE_ENV !== 'production';
    }
    return i.isSupported();
  }

  // =========================================================================
  // App-Installation (Service-Funktion, Seite /info-apps)
  // =========================================================================

  // Alle App-Ids, die auf diesem Geraet vorhanden sein sollen:
  // die konfigurierten OTT-Apps plus die nativen Cast-Einstiege (nur LG - auf
  // Samsung ist Google Cast Teil der Firmware und keine installierbare App).
  static getInstallTargets(options) {
    const opts = options || {};
    const platform = this.platform;
    const lang = opts.lang || null;
    // Voreinstellung ist der VOLLE Katalog, nicht die sichtbaren Kacheln: die
    // Installationsseite soll auch die Apps pruefen, die per Google Cast
    // erreicht werden und deshalb keine eigene Kachel haben. Eintraege ohne
    // App-Id werden ueber ihre aliases am Geraet gesucht; die generische
    // Cast-Kachel (weder Id noch aliases) faellt unten von selbst raus.
    const apps = opts.apps && opts.apps.length ? opts.apps : CAST_APP_CATALOG;

    const targets = [];
    apps.forEach((app) => {
      const appIds = appIdCandidates(app, platform, lang);
      const appId = appIds[0] || '';
      // Ohne Id bleibt die Namenssuche - Apps ohne Id UND ohne Namen (die
      // generische Cast-Kachel) haben am Geraet nichts zu suchen.
      if (!appId && !(app.aliases && app.aliases.length)) return;
      const byLang = platform === 'lg' ? app.lgAppIdByLang : app.tizenAppIdByLang;
      targets.push({
        key: app.key,
        name: app.name,
        appId: appId,
        // Alle Kandidaten - getInstallPlan fragt jede ab und waehlt.
        appIds: appIds,
        aliases: app.aliases || null,
        group: 'OTT',
        // Gesetzt, wenn die Id aus der Sprachvariante stammt - so ist auf der
        // Seite erkennbar, dass hier eine sprachspezifische App verwendet wird.
        lang: byLang && lang && byLang[lang] ? lang : null,
      });
    });

    if (platform === 'lg') {
      LG_GOOGLE_CAST_APP_IDS.forEach((id) => {
        // Keine Aliasse: die Cast-Ids sind belegt, eine Namenssuche nach "cast"
        // wuerde nur falsche Treffer liefern.
        targets.push({ key: id, name: 'Google Cast', appId: id, aliases: null, group: 'Cast', lang: null });
      });
    } else if (platform === 'tizen') {
      targets.push({
        key: TIZEN_GOOGLE_CAST_APP_ID,
        name: 'Google Cast',
        appId: TIZEN_GOOGLE_CAST_APP_ID,
        aliases: null,
        group: 'Cast',
        lang: null,
      });
    }
    return targets;
  }

  // Ermittelt zu jedem Ziel, was das Geraet dazu meldet. Ergebnis pro Eintrag:
  //   installed   - vorhanden und startbar
  //   installable - bekannt, aber nicht installiert -> Installation versuchen
  //   blocked     - vom Geraet gesperrt (Samsung CAPABILITY_DISABLE)
  //   missing     - dem Geraet unbekannt -> Installation unmoeglich
  //   unknown     - keine Auskunft moeglich
  static getInstallPlan(options) {
    const targets = this.getInstallTargets(options);
    const i = impl();
    if (!i || typeof i.getInstallState !== 'function') {
      return Promise.resolve(
        targets.map((t) => Object.assign({}, t, { state: 'unknown', detail: 'keine Geraete-API' }))
      );
    }
    return this.detect()
      .catch(() => null)
      .then(() =>
        Promise.all(
          targets.map((t) => Cast._stateForTarget(i, t))
        )
      );
  }

  // Der Zustand eines Ziels. Mit mehreren Ids wird jede abgefragt und die
  // nach chooseAppId passende genommen; `candidates` zeigt auf /info-apps, was
  // das Geraet zu jeder einzelnen gemeldet hat.
  static _stateForTarget(i, t) {
    const ask = (id) =>
      Promise.resolve()
        .then(() => i.getInstallState(id, { name: t.name, aliases: t.aliases }))
        .then((s) => Object.assign({ id: id }, s))
        .catch((e) => ({ id: id, state: 'unknown', detail: String(e && e.message) }));
    const ids = t.appIds && t.appIds.length > 1 ? t.appIds : [t.appId];
    return Promise.all(ids.map(ask)).then((states) => {
      const picked = chooseAppId(states) || { state: 'unknown' };
      const out = Object.assign({}, t, picked, { appId: picked.id || t.appId });
      delete out.id;
      if (ids.length > 1) {
        out.candidates = states.map((c) => ({ id: c.id, state: c.state }));
        out.detail = (picked.detail ? picked.detail + ' · ' : '') + 'Id ' + out.appId + ' von ' + ids.join(', ');
      }
      return out;
    });
  }

  // Die Id, unter der eine App auf DIESEM Geraet zu starten ist. Bei mehreren
  // Kandidaten wird das Geraet gefragt (installiert vor installierbar); bei
  // einer bleibt es ohne Rueckfrage bei ihr. '' = keine Id im Katalog.
  static pickAppId(app, lang) {
    const i = impl();
    const ids = appIdCandidates(app, this.platform, lang);
    if (ids.length < 2 || !i || typeof i.getInstallState !== 'function') return Promise.resolve(ids[0] || '');
    return this._stateForTarget(i, { name: app.name, aliases: app.aliases, appId: ids[0], appIds: ids })
      .then((st) => st.resolvedAppId || st.appId || ids[0])
      .catch(() => ids[0]);
  }

  // Installiert alle Eintraege mit state 'installable', der Reihe nach.
  // Sequenziell und nicht parallel: die Installation belastet Netz und Geraet,
  // und bei parallelen Aufrufen laesst sich ein Fehlschlag nicht mehr sauber
  // einer App zuordnen. onProgress(entry, index, total) meldet den Fortschritt.
  static installPlan(plan, onProgress) {
    const i = impl();
    const entries = (plan || []).slice();
    const total = entries.length;
    const results = [];

    const step = (index) => {
      if (index >= total) return Promise.resolve(results);
      const entry = entries[index];
      const report = (result) => {
        const merged = Object.assign({}, entry, result);
        results.push(merged);
        if (onProgress) onProgress(merged, index + 1, total);
        return step(index + 1);
      };

      if (entry.state !== 'installable') {
        // Nichts zu tun - Zustand unveraendert uebernehmen.
        return report({ action: 'skipped' });
      }
      if (!i || typeof i.installApp !== 'function') {
        return report({ action: 'failed', state: 'unknown', detail: 'keine Install-API' });
      }
      // resolvedAppId gewinnt: wurde die App unter abweichender Id am Geraet
      // gefunden, muss diese installiert werden, nicht die konfigurierte.
      const installId = entry.resolvedAppId || entry.appId;
      return Promise.resolve()
        .then(() => i.installApp(installId))
        .then(() => report({ action: 'installed', state: 'installed', detail: 'installiert als ' + installId }))
        .catch((err) =>
          report({
            action: 'failed',
            state: 'installable',
            detail: String((err && (err.errorMessage || err.message)) || err),
          })
        );
    };

    return step(0);
  }

  // Vollstaendiges Diagnose-Objekt fuer die Info-Seite. Enthaelt bewusst auch
  // die Rohwerte der Versionserkennung, damit eine falsche Zuordnung auf
  // echter Hardware sofort sichtbar ist.
  static getDiagnostics() {
    const caps = this.capabilities();
    return {
      platform: this.platform,
      strategy: this.castStrategy(),
      supported: this.isSupported(),
      nativeCast: this.platform === 'lg' ? CastLG.isNativeCastSupported() : false,
      webos: DeviceType.getWebOSVersion,
      webosMajor: DeviceType.getWebOSMajor,
      webosInfo: DeviceType.getWebOSInfo,
      capabilities: caps,
      // LIVE erhoben, nicht aus den Capabilities: die stammen aus detect() und
      // damit aus der Zeit vor der ersten Verbindung. Gesucht ist aber genau
      // die Funktion, deren Wert sich AENDERT, sobald ein Handy am SoftAP
      // haengt - ein Schnappschuss von vorher zeigt das nicht.
      softApProbe: this.softApProbe(),
      // Ebenfalls LIVE: laeuft der Hotspot ueberhaupt? Genau diese Frage war am
      // Geraet nicht zu beantworten, als er nach einem Power-Zyklus aus blieb.
      softApEnabled: this.isSoftApEnabled(),
    };
  }

  // Sendet der Hotspot NICHT? true / false / null (keine Auskunft).
  //
  // Plattformuebergreifend, und bewusst getrennt von softApIsDown: dort ist die
  // Frage "lag ein Power-Off dazwischen", was nur auf LG aus soft_ap == 0 folgt.
  // Hier ist die Frage "kann sich ueberhaupt jemand verbinden" - und die stellt
  // sich auf beiden Plattformen, weil ein QR ohne sendenden Hotspot ins Nichts
  // fuehrt.
  //   Samsung: isSoftAPEnabled bzw. als Ersatz die Signalstaerke.
  //   LG:      die Property soft_ap. Auf 0 betreibt der TV keinen Hotspot -
  //            unabhaengig davon, WARUM sie auf 0 steht.
  static softApNotSending() {
    const i = impl();
    if (i && typeof i.isSoftApEnabled === 'function') {
      const up = i.isSoftApEnabled();
      if (up === null || typeof up === 'undefined') return Promise.resolve(null);
      return Promise.resolve(up === false);
    }
    if (i && typeof i.softApIsDown === 'function') {
      try {
        return Promise.resolve(i.softApIsDown()).catch(() => null);
      } catch (e) {
        return Promise.resolve(null);
      }
    }
    return Promise.resolve(null);
  }

  // Der Schluessel, den das GERAET gespeichert hat. Nach einem Neustart ist das
  // genau der, mit dem der Hotspot sendet - deshalb ist er dann die Wahrheit.
  static readSoftApKey() {
    const i = impl();
    if (!i || typeof i.getSoftAPSecurityKey !== 'function') return null;
    try {
      return i.getSoftAPSecurityKey() || null;
    } catch (e) {
      return null;
    }
  }

  // Laeuft der Hotspot? null = das Geraet gibt keine Auskunft. Synchron und
  // deshalb nur auf Samsung aussagekraeftig - fuer die Anzeige-Entscheidung
  // softApNotSending benutzen.
  static isSoftApEnabled() {
    const i = impl();
    if (!i || typeof i.isSoftApEnabled !== 'function') return null;
    return i.isSoftApEnabled();
  }

  // Bringt den Hotspot zurueck, falls er unten ist - mit Wiederholungen.
  // Ausdruecklich nur fuer Hintergrundarbeit: ein Versuch kostet auf manchen
  // Firmwares zehn Sekunden.
  static ensureSoftApUp() {
    const i = impl();
    if (!i || typeof i.ensureSoftApUp !== 'function') return Promise.resolve(null);
    try {
      return Promise.resolve(i.ensureSoftApUp());
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  // Alle lesenden SoftAP-Funktionen des Geraets mit ihrer aktuellen Antwort.
  // Nur Samsung implementiert das; sonst leer.
  static softApProbe() {
    const i = impl();
    if (!i || typeof i.softApProbe !== 'function') return [];
    return i.softApProbe();
  }

  // Deutet die Nutzlast des SoftAP-State-Listeners.
  //
  // Auf einem BU800 (Tizen 6.5) kommt pro Client-Ereignis ein Objekt:
  //   {"ClientName":"OnePlus-13","IPAddress":"192.168.43.173",
  //    "MacAddress":"de:1b:fb:b2:00:da","Band":0,"State":1}
  // State 1 = verbunden, 0 = getrennt. Die Feldnamen beginnen GROSS - deshalb
  // wird hier case-insensitiv gesucht; ein Zugriff auf v.state waere undefined.
  //
  // Samsung legt das Format nicht fest, andere Firmwares liefern eine Zahl
  // (Anzahl Clients), einen Zustands-String ("STA_CONNECTED"/"DISCONNECTED")
  // oder ein Objekt mit einem dieser Werte, teils in .data verschachtelt.
  // Deshalb bleibt die Auswertung breit - und der Rohwert wird mitgeliefert,
  // damit auf echter Hardware sichtbar ist, was ankam.
  //
  // Ergebnis: { connected, clients, raw, client: {name, ip, mac} | null }.
  // clients ist die Angabe AUS DIESEM Ereignis; ueber mehrere Geraete zaehlt
  // der Store anhand der MAC (siehe cast/watchSoftAP).
  static readSoftAPState(payload) {
    const raw = (function stringify(v) {
      if (v === null || v === undefined) return String(v);
      if (typeof v === 'object') {
        try {
          return JSON.stringify(v);
        } catch (e) {
          return String(v);
        }
      }
      return String(v);
    })(payload);

    // Manche Firmwares uebergeben das Objekt als JSON-String.
    let value = payload;
    if (typeof value === 'string') {
      const t = value.trim();
      if (t.charAt(0) === '{' || t.charAt(0) === '[') {
        try {
          value = JSON.parse(t);
        } catch (e) {
          /* bleibt String - unten als Zustands-String ausgewertet */
        }
      }
    }

    // Feld case-insensitiv holen: "State", "state" und "STATE" sind dasselbe.
    const field = (obj, names) => {
      const keys = Object.keys(obj);
      for (let i = 0; i < names.length; i++) {
        for (let k = 0; k < keys.length; k++) {
          if (keys[k].toLowerCase() === names[i]) return obj[keys[k]];
        }
      }
      return undefined;
    };

    const client = (obj) => {
      const name = field(obj, ['clientname', 'name', 'devicename']);
      const ip = field(obj, ['ipaddress', 'ip']);
      const mac = field(obj, ['macaddress', 'mac', 'bssid']);
      if (name === undefined && ip === undefined && mac === undefined) return null;
      return {
        name: name === undefined ? '' : String(name),
        ip: ip === undefined ? '' : String(ip),
        mac: mac === undefined ? '' : String(mac),
      };
    };

    const unwrap = (v, depth) => {
      if (depth > 3) return { connected: false, clients: 0, client: null };
      if (typeof v === 'number') return { connected: v > 0, clients: v, client: null };
      // Ein blankes true/false ist der Zustand des SoftAP, nicht eines Clients -
      // siehe Begruendung beim String-Zweig. Nur false ist eindeutig.
      if (typeof v === 'boolean') return { connected: false, clients: 0, client: null };
      if (typeof v === 'string') {
        const s = v.toUpperCase();
        // "DISCONNECTED" enthaelt "CONNECTED" - erst auf ab-/getrennt pruefen.
        if (s.indexOf('DISCONNECT') !== -1 || s.indexOf('DETACH') !== -1) {
          return { connected: false, clients: 0, client: null };
        }
        if (s.indexOf('CONNECT') !== -1 || s.indexOf('ATTACH') !== -1) {
          return { connected: true, clients: 1, client: null };
        }
        // Ein blankes ON/OFF/ENABLED meint den SoftAP SELBST, nicht ein
        // verbundenes Geraet. Als "verbunden" auslegen wuerde die Seite sofort
        // nach dem Aktivieren "Smartphone verbunden" melden - also ausdruecklich
        // NICHT. Nur ein Client-Zaehler oder ein Connect-Ereignis zaehlt.
        const n = parseInt(s, 10);
        if (!isNaN(n) && String(n) === s.trim()) {
          return { connected: n > 0, clients: n, client: null };
        }
        return { connected: false, clients: 0, client: null };
      }
      // Eine Liste ist der Normalfall bei einer Client-ABFRAGE (im Gegensatz zum
      // Ereignis): sie ist ein vollstaendiger Ist-Stand, ihre Laenge also die
      // Anzahl der verbundenen Geraete.
      if (Array.isArray(v)) {
        const last = v.length ? v[v.length - 1] : null;
        return {
          connected: v.length > 0,
          clients: v.length,
          client: last && typeof last === 'object' ? client(last) : null,
        };
      }
      if (v && typeof v === 'object') {
        const info = client(v);

        // Client-Ereignis mit Zustand - der Normalfall auf Tizen 6.5.
        const state = field(v, ['state', 'status', 'connected']);
        if (state !== undefined) {
          const inner = unwrap(state, depth + 1);
          return {
            connected: inner.connected,
            clients: inner.clients,
            client: info,
          };
        }

        const count = field(v, ['numberofdevices', 'devicecount', 'clientcount', 'stationcount', 'count']);
        if (typeof count === 'number') {
          return { connected: count > 0, clients: count, client: info };
        }

        const list = field(v, ['devicelist', 'clients', 'stations', 'list']);
        if (Array.isArray(list)) {
          return { connected: list.length > 0, clients: list.length, client: info };
        }

        const nested = field(v, ['data']);
        if (nested !== undefined) {
          const inner = unwrap(nested, depth + 1);
          return {
            connected: inner.connected,
            clients: inner.clients,
            client: inner.client || info,
          };
        }

        // Nur Client-Daten ohne Zustandsfeld: dass der TV ueberhaupt ueber
        // diesen Client berichtet, heisst, dass er sich verbunden hat.
        if (info) return { connected: true, clients: 1, client: info };
      }
      return { connected: false, clients: 0, client: null };
    };

    return Object.assign({ raw: raw }, unwrap(value, 0));
  }

  // Fuehrt ein einzelnes SoftAP-Ereignis mit den bereits bekannten Geraeten
  // zusammen. `known` ist eine Map MAC -> Anzeigename; zurueck kommt die neue
  // Map plus alles, was die Anzeige braucht.
  //
  // Notwendig, weil der TV pro Geraet ein eigenes Ereignis mit State 1/0 meldet
  // und keinen Gesamtstand. Ohne Mitzaehlen wuerde das "getrennt" des einen
  // Handys die Anzeige loeschen, obwohl noch ein zweites verbunden ist.
  //
  // Ohne MAC im Ereignis (andere Firmware, Zaehler- oder String-Nutzlast) bleibt
  // es beim Wert des Ereignisses selbst.
  static mergeSoftAPClients(known, info) {
    const mac = (info && info.client && info.client.mac) || '';
    const eventName = (info && info.client && info.client.name) || '';
    if (!mac) {
      return {
        known: Object.assign({}, known),
        connected: !!(info && info.connected),
        clients: (info && info.clients) || 0,
        name: eventName,
      };
    }

    const next = Object.assign({}, known);
    if (info.connected) next[mac] = eventName || mac;
    else delete next[mac];

    const macs = Object.keys(next);
    // Bei einem "getrennt" traegt das Ereignis den Namen des GEGANGENEN Geraets -
    // dann den eines noch verbundenen anzeigen. Schluesselreihenfolge =
    // Einfuegereihenfolge, also ist der letzte der zuletzt hinzugekommene.
    const name = info.connected && eventName ? eventName : macs.length ? next[macs[macs.length - 1]] : '';
    return { known: next, connected: macs.length > 0, clients: macs.length, name: name };
  }

  // Baut die WiFi-QR-Nutzlast im Standardformat, das Android/iOS-Kameras direkt
  // als "WLAN beitreten" erkennen. Bei offenem Netz -> T:nopass.
  static buildWifiQrPayload({ ssid, key, security, hidden }) {
    const esc = (s) =>
      String(s == null ? '' : s).replace(/([\\;,:"])/g, '\\$1');
    const sec = key ? security || 'WPA' : 'nopass';
    var payload = 'WIFI:T:' + sec + ';S:' + esc(ssid) + ';';
    if (key) payload += 'P:' + esc(key) + ';';
    if (hidden) payload += 'H:true;';
    payload += ';';
    return payload;
  }

  // ---- Durchreichen an die Plattform-Implementierung -----------------------
  static initToken(token) {
    const i = impl();
    return i ? i.initToken(token || DEFAULT_CAST_TOKEN) : false;
  }
  static getCapability(appId) {
    const i = impl();
    return i ? i.getCapability(appId) : null;
  }
  static installApp(appId, onProgress) {
    const i = impl();
    return i ? i.installApp(appId, onProgress) : Promise.reject(new Error('no cast impl'));
  }
  // Sucht die App-Id einer Katalog-App unter den INSTALLIERTEN Apps des TV -
  // ueber Name und aliases. Fuer Eintraege ohne belegte Id (DAZN, Joyn, bis die
  // clone.xml ausgewertet ist). '' = nicht gefunden bzw. Plattform kann es nicht.
  static findInstalledAppId(app) {
    const i = impl();
    if (!app || !i || typeof i.findInstalledAppId !== 'function') return Promise.resolve('');
    return i.findInstalledAppId({ name: app.name, aliases: app.aliases }).catch(() => '');
  }
  static launchApp(appId, opts) {
    const i = impl();
    return i ? i.launchApp(appId, opts) : Promise.reject(new Error('no cast impl'));
  }

  static configureSoftAP(config) {
    const i = impl();
    if (!i) {
      const c = config || {};
      return { ssid: c.ssid || 'HotelTV', key: c.key || '', security: c.security || 'WPA' };
    }
    return i.configureSoftAP(config);
  }
  // Geraetename, unter dem der TV in Cast-/AirPlay-Listen erscheint.
  //
  // Auf LG ist das die Property tv_name - und die benutzt auch LGs eigene
  // Cast-App als Netzkennung, wenn sie ihren Hotspot aufspannt. Ohne Setzen
  // heissen baugleiche Geraete in Nachbarzimmern alle gleich.
  //
  // Auf Samsung gibt es dafuer keinen Setter in den TEP-APIs; dort traegt die
  // SoftAP-SSID (setSoftAPSSID in configureSoftAP) die Raumkennung.
  static getDeviceName() {
    const i = impl();
    if (i && typeof i.getTVName === 'function') return i.getTVName();
    return null;
  }
  // Steht der gewuenschte Geraetename schon auf dem TV - auch wenn die Firmware
  // ihm ein Praefix voranstellt? Ohne diese Frage schreibt applyDeviceName bei
  // jedem Start erneut und scheitert jedes Mal.
  static deviceNameMatches(name) {
    const i = impl();
    if (i && typeof i.tvNameMatches === 'function') return i.tvNameMatches(name);
    return Cast.getDeviceName() === name;
  }
  static setDeviceName(name) {
    const i = impl();
    if (!i || typeof i.setTVName !== 'function' || !name) {
      return Promise.resolve(false);
    }
    return Promise.resolve(i.setTVName(name))
      .then(() => true)
      .catch(() => false);
  }

  // Vergibt ein neues SoftAP-Passwort und schreibt es auf den TV. Gehoert zum
  // Gastwechsel: ohne das bleibt der Schluessel auf Samsung fuer immer derselbe
  // (configureSoftAP bevorzugt den bereits gespeicherten), und ein abgereister
  // Gast koennte sich weiter mit dem SoftAP verbinden.
  //
  // Immer ein Promise, damit der Aufrufer beide Plattformen gleich behandeln
  // kann - Tizen antwortet synchron, LG ueber HCAP.
  static rotateSoftAP(newKey) {
    const i = impl();
    if (!i || typeof i.rotateSoftAP !== 'function') {
      return Promise.resolve({ ok: false, key: '', detail: 'auf dieser Plattform nicht moeglich' });
    }
    try {
      return Promise.resolve(i.rotateSoftAP(newKey));
    } catch (e) {
      return Promise.resolve({ ok: false, key: '', detail: String((e && e.message) || e) });
    }
  }

  // Ist der SoftAP unten, obwohl wir ihn eingeschaltet hatten?
  //
  // Auf LG ist das der einzige verlaessliche Nachweis, dass der TV aus war: die
  // Property soft_ap faellt laut HCAP-Doku "in power-off sequence" auf 0 zurueck
  // und ueberlebt damit weder Standby noch Reboot. Steht sie beim Aufwachen auf
  // 0, war der TV aus - kam die App dagegen nur aus einer Fremd-App zurueck,
  // laeuft der Hotspot weiter und die Property steht auf 1.
  //
  // Liefert true / false / null, wobei null "keine Aussage moeglich" heisst
  // (Samsung: dort melden die Power-Listener den Standby direkt).
  // Den Beobachter starten, der einen Abfall von soft_ap auf 0 festhaelt.
  // Nur LG hat ein Pendant; sonst No-op.
  // `onDrop` laeuft, SOBALD der Abfall gesehen wurde - also waehrend der TV aus
  // ist. Dort gehoert der Passwortwechsel hin (siehe stageSoftApKey).
  static watchSoftApDrop(onDrop) {
    const i = impl();
    if (!i || typeof i.watchSoftApDrop !== 'function') return false;
    return i.watchSoftApDrop(onDrop);
  }
  // Wurde seit dem letzten Aufruf ein Abfall gesehen? Holt die Antwort ab.
  //
  // Belastbarer als softApIsDown(): dort wird beim Aufwachen NACHGESEHEN, und
  // bis dahin hat die Firmware den Hotspot oft schon wieder hochgefahren.
  static takeSoftApDownMark() {
    const i = impl();
    if (!i || typeof i.takeSoftApDownMark !== 'function') return false;
    return i.takeSoftApDownMark();
  }
  static softApIsDown() {
    const i = impl();
    if (!i || typeof i.softApIsDown !== 'function') return Promise.resolve(null);
    try {
      return Promise.resolve(i.softApIsDown()).catch(() => null);
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  // Schreibt einen neuen SoftAP-Schluessel, ohne den Hotspot anzufassen - er
  // gilt dann ab dem naechsten Neustart des TV. Nur fuer Geraete, die ihren
  // Hotspot nicht neu starten koennen; sonst rotiert rotateSoftAP direkt.
  // Liefert ein Promise<boolean>: auf LG ist das Schreiben ein hcap-Aufruf und
  // kann nicht synchron beantwortet werden. Ohne Pendant sofort false.
  static stageSoftApKey(key) {
    const i = impl();
    if (!i || typeof i.stageSoftApKey !== 'function') return Promise.resolve(false);
    try {
      return Promise.resolve(i.stageSoftApKey(key))
        .then((ok) => !!ok)
        .catch(() => false);
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  // Kann dieses Geraet seinen Hotspot ueberhaupt neu starten? false heisst:
  // ein Passwortwechsel braucht einen Neustart des TV.
  static canRestartSoftAp() {
    const i = impl();
    if (!i || typeof i.isSoftApSwitchBroken !== 'function') return true;
    return !i.isSoftApSwitchBroken();
  }

  // Schaltet den SoftAP ab, ohne ihn neu zu starten.
  //
  // Fuer den Ausschaltpfad: ein Aufruf statt eines vollstaendigen Neustarts.
  // Wieder eingeschaltet wird beim Aufwachen - dort wird auch das neue Passwort
  // wirksam (rotateSoftAP). Liefert immer ein Promise, damit der Aufrufer beide
  // Plattformen gleich behandeln kann: Tizen antwortet synchron, LG ueber HCAP.
  static disableSoftAP() {
    const i = impl();
    if (!i || typeof i.disableSoftAP !== 'function') return Promise.resolve(false);
    try {
      return Promise.resolve(i.disableSoftAP());
    } catch (e) {
      return Promise.resolve(false);
    }
  }

  // Prueft nach dem Schreiben, ob der TV SSID und Passwort uebernommen hat.
  //
  // Beide Plattformen versuchen es, beide koennen scheitern: auf LG ist die SSID
  // die Property tv_name und das Passwort soft_ap_password (lesbar erst ab
  // IDPN 304), auf Samsung existieren Getter je nach Firmware unter
  // wechselnden Namen. Deshalb ist das Ergebnis dreiwertig - readable bzw.
  // keyReadable=false bedeutet ausdruecklich "keine Aussage moeglich", nicht
  // "falsch". Nur bei readable && !matched ist der gelesene Wert der echte.
  static verifySoftAP(expectedSsid, expectedKey) {
    const i = impl();
    if (i && typeof i.verifySoftAP === 'function') return i.verifySoftAP(expectedSsid, expectedKey);
    return Promise.resolve({
      ssid: '',
      expected: String(expectedSsid == null ? '' : expectedSsid),
      readable: false,
      matched: false,
      key: '',
      expectedKey: String(expectedKey == null ? '' : expectedKey),
      keyReadable: false,
      keyMatched: false,
      detail: 'auf dieser Plattform nicht pruefbar',
    });
  }
  static addSoftAPStateChangeListener(cb) {
    const i = impl();
    return i ? i.addSoftAPStateChangeListener(cb) : -1;
  }

  // Meldet der TV, WELCHES Geraet sich mit dem SoftAP verbunden hat?
  //
  // Das ist die Quelle fuer "Smartphone verbunden" - nicht
  // addSoftAPStateChangeListener, der den Zustand des Hotspots selbst meldet.
  // Auf Tizen 9.0 heisst sie addSoftAPClientInfoListener; LG hat kein Pendant.
  static hasSoftAPClientInfoListener() {
    const i = impl();
    if (!i || typeof i.hasSoftAPClientInfoListener !== 'function') return false;
    return i.hasSoftAPClientInfoListener();
  }
  static addSoftAPClientInfoListener(cb) {
    const i = impl();
    if (!i || typeof i.addSoftAPClientInfoListener !== 'function') return -1;
    return i.addSoftAPClientInfoListener(cb);
  }
  static removeSoftAPClientInfoListener(cb) {
    const i = impl();
    if (!i || typeof i.removeSoftAPClientInfoListener !== 'function') return false;
    return i.removeSoftAPClientInfoListener(cb);
  }

  // Fragt den IST-Stand der am SoftAP verbundenen Geraete ab.
  //
  // Ergaenzung zum Listener, der nur AENDERUNGEN meldet: hing das Handy schon,
  // bevor die Cast-Seite den Listener registriert hat, kommt nie ein Ereignis
  // und die Anzeige bleibt fuer immer auf "warte auf Verbindung".
  //
  // Liefert eine LISTE [{ fn, value }] aller Funktionen, die dafuer in Frage
  // kommen - welche es wirklich ist, gibt Samsung nicht her und wird am Wert
  // entschieden (siehe cast/watchSoftAP). Leere Liste = keine Auskunft moeglich.
  static getSoftAPClients() {
    const i = impl();
    if (!i || typeof i.getSoftAPClients !== 'function') return [];
    return i.getSoftAPClients() || [];
  }
  // Kann das Geraet nach verbundenen Clients gefragt werden?
  //
  // Bewusst die EXISTENZ der Funktionen und nicht ihr aktueller Wert: gefragt
  // wird beim Oeffnen der Cast-Seite, und da haengt noch kein Handy am Hotspot.
  // Vorher stand hier getSoftAPClients().length > 0 - damit galt das Geraet als
  // unfaehig, die Abfrage wurde nie gestartet und die Anzeige blieb fuer immer
  // auf "warte auf Verbindung".
  static hasSoftAPClientQuery() {
    const i = impl();
    if (i && typeof i.hasSoftAPClientFns === 'function') return i.hasSoftAPClientFns();
    return Cast.getSoftAPClients().length > 0;
  }
  // Meldet der TV ueberhaupt, wenn sich jemand mit dem SoftAP verbindet?
  // Nur Samsung tut das (webapis.network); LG liefert -1, dort bleibt die
  // Anzeige bei der reinen Anleitung statt bei einem "warte auf Verbindung",
  // das nie umschlagen wuerde.
  static hasSoftAPStateListener() {
    const i = impl();
    if (!i || typeof i.hasSoftAPStateListener !== 'function') return false;
    return i.hasSoftAPStateListener();
  }
  static removeSoftAPStateChangeListener(cb) {
    const i = impl();
    return i ? i.removeSoftAPStateChangeListener(cb) : false;
  }

  static updateFirmware(fw, onProgress) {
    const i = impl();
    return i ? i.updateFirmware(fw, onProgress) : Promise.reject(new Error('no cast impl'));
  }
  static unsetFirmwareListener() {
    const i = impl();
    if (i) i.unsetFirmwareListener();
  }
  static restoreClone(path, onProgress) {
    const i = impl();
    return i ? i.restoreClone(path, onProgress) : Promise.reject(new Error('no cast impl'));
  }
  static unsetCloneListener() {
    const i = impl();
    if (i) i.unsetCloneListener();
  }
  static clearCredentials(value) {
    const i = impl();
    return i ? i.clearCredentials(value) : false;
  }
  // Loeschen beim naechsten Neustart vormerken, ohne jetzt schon etwas zu
  // loeschen. Nur Samsung kennt diese Trennung (deleteAppPrivateInfo("ON"));
  // auf LG ist der Checkout immer sofort, dort ist das ein No-op.
  static armClearCredentials() {
    const i = impl();
    if (i && typeof i.armClearCredentials === 'function') return i.armClearCredentials();
    return false;
  }
  // Kennt das Geraet das SOFORTIGE Loeschen, oder nur das Vormerken fuer den
  // naechsten Neustart? Auf aelteren Samsung-Firmwares (und am U800F/Tizen 9.0)
  // fehlt deleteAppPrivateInfoNow - dann ist ein Neustart der einzige Weg, und
  // das Tor vor dem App-Start darf nicht darauf warten.
  static hasClearCredentialsNow() {
    const i = impl();
    if (!i || typeof i.hasClearCredentialsNow !== 'function') return true;
    return i.hasClearCredentialsNow();
  }
  static clearCredentialsNow() {
    const i = impl();
    return i ? i.clearCredentialsNow() : Promise.reject(new Error('no cast impl'));
  }
  // Ist ein gelungener Loeschaufruf auch ein NACHWEIS, dass die Daten weg sind?
  //
  // Auf LG nicht: requestCheckout quittiert mit result:true, setzt aber auf den
  // Checkout-Snapshot zurueck - und solange keiner genommen wurde, gibt es
  // nichts zurueckzusetzen (siehe Cast/LG.js clearCredentialsNow). Wo die
  // Plattform nichts Gegenteiliges sagt, gilt der Aufruf wie bisher als Beleg.
  // true = belegt wirksam, false = belegt NICHT wirksam (LG ohne Snapshot),
  // null = nicht geprueft (Samsung, siehe Cast/Tizen.js). Wer nur wissen will,
  // ob ein Nachweis vorliegt, prueft auf === true.
  static isClearCredentialsVerified() {
    const i = impl();
    if (!i || typeof i.isClearCredentialsVerified !== 'function') return true;
    return i.isClearCredentialsVerified();
  }
  // Zeigt der Loeschbefehl seine Wirkung erst nach einem Neustart?
  //
  // Auf LG ja (siehe Cast/LG.js clearTakesEffectOnReboot). Wo die Plattform
  // nichts sagt, gilt wie bisher: der Aufruf wirkt sofort.
  static clearTakesEffectOnReboot() {
    const i = impl();
    if (!i || typeof i.clearTakesEffectOnReboot !== 'function') return false;
    return i.clearTakesEffectOnReboot();
  }
  // Traegt den persistierten Snapshot-Merker in den Plattform-Helfer ein.
  static noteCheckoutSnapshot(taken) {
    const i = impl();
    if (i && typeof i.noteCheckoutSnapshot === 'function') return i.noteCheckoutSnapshot(taken);
    return false;
  }
  // Braucht diese Plattform ueberhaupt einen Checkout-Snapshot? Nur LG.
  static needsCheckoutSnapshot() {
    const i = impl();
    return !!(i && typeof i.takeCheckoutSnapshot === 'function');
  }
  // Nimmt den Snapshot, auf den requestCheckout spaeter zurueckSETZT. Der
  // Aufrufer verantwortet den Zeitpunkt - siehe cast/takeCheckoutSnapshot.
  static takeCheckoutSnapshot() {
    const i = impl();
    if (!i || typeof i.takeCheckoutSnapshot !== 'function') {
      return Promise.resolve(false);
    }
    return Promise.resolve(i.takeCheckoutSnapshot()).then(() => true);
  }
  static prepareForCheckIn(token) {
    const i = impl();
    return i ? i.prepareForCheckIn(token || DEFAULT_CAST_TOKEN) : false;
  }
  // Die Plattform-Flags, ohne die das Handy den TV gar nicht erst findet:
  // DIAL (Google Cast startet Apps auf dem TV), Screen Share, CP-App-Modus.
  //
  // Eigener Einstieg neben prepareForCheckIn, weil sie NICHT nur zum Check-in
  // gehoeren: sie fallen bei einem Neustart und bei einem Checkout zurueck und
  // muessen dann erneut gesetzt werden, ohne dass gleich die ganze
  // Check-in-Vorbereitung laeuft. Auf Plattformen ohne Pendant ein No-op.
  static enableCasting() {
    const i = impl();
    if (!i || typeof i.enableCasting !== 'function') return Promise.resolve(false);
    return Promise.resolve(i.enableCasting()).then(() => true).catch(() => false);
  }
  static finalizeForCheckOut() {
    const i = impl();
    return i ? i.finalizeForCheckOut() : false;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/Cast/index.js